import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Languages, LogOut, Monitor, Moon, Sun, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme, type Theme } from '@/components/theme-provider';
import { useAuthStore } from '@/features/auth/auth.store';
import { SUPPORTED_LANGUAGES, type Language } from '@/i18n';
import { useLanguage } from '@/i18n/use-language';

function initials(first?: string, last?: string) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || '?';
}

export function UserMenu() {
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!profile) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="focus-visible:ring-ring/50 flex items-center gap-2 rounded-full outline-none focus-visible:ring-2">
        <Avatar className="size-8">
          {profile.avatar && <AvatarImage src={profile.avatar} alt="" />}
          <AvatarFallback>
            {initials(profile.firstName, profile.lastName)}
          </AvatarFallback>
        </Avatar>
        <span className="hidden text-sm font-medium sm:inline">
          {profile.firstName}
        </span>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-foreground py-2 text-sm font-normal">
          <span className="block font-semibold">
            {profile.firstName} {profile.lastName}
          </span>
          <span className="text-muted-foreground block truncate text-xs">
            {profile.email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => navigate('/profile')}>
          <User />
          {t('shell.profile')}
        </DropdownMenuItem>

        {/* The header carries a language control too, but it is hidden on the
            narrowest screens — the choice has to exist on both paths. */}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('common.language')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={language}
          onValueChange={(value) => void setLanguage(value as Language)}
        >
          {SUPPORTED_LANGUAGES.map((code) => (
            <DropdownMenuRadioItem key={code} value={code}>
              <Languages />
              {code === 'en' ? t('common.english') : t('common.spanish')}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('shell.appearance')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => setTheme(value as Theme)}
        >
          <DropdownMenuRadioItem value="light">
            <Sun />
            {t('shell.themeLight')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon />
            {t('shell.themeDark')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor />
            {t('shell.themeSystem')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={signOut}>
          <LogOut />
          {t('common.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
